import React, { useContext } from "react";

import { add, set, sub } from "date-fns";

import { useAppSelector } from "app/hooks";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { getFilterDateRange } from "features/marketing/touchpoints/touchpoints.utils";
import useDeferredReady from "hooks/use-deferred-ready.hook";
import { useGetAssignedTouchpointsCountQuery } from "services/touchpoints";

const TouchpointsCountContext = React.createContext<{
  assignedTouchpointsCount: number;
} | null>(null);

type TouchpointsCountProviderProps = {
  children?: React.ReactNode;
};

export function TouchpointsCountProvider({
  children
}: TouchpointsCountProviderProps) {
  const { after_date_iso, before_date_iso } = getFilterDateRange(
    set(sub(new Date(), { days: 7 }), { hours: 0, minutes: 0, seconds: 0 }),
    set(add(new Date(), { days: 7 }), { hours: 23, minutes: 59, seconds: 59 })
  );

  const currentClinic = useAppSelector(selectCurrentClinic);
  const deferredReady = useDeferredReady();

  const { data: assignedTouchpointsCount = 0 } =
    useGetAssignedTouchpointsCountQuery(
      {
        clinic: currentClinic?.id,
        after_execution_time: after_date_iso,
        before_execution_time: before_date_iso
      },
      {
        skip: !deferredReady || !currentClinic?.id,
        pollingInterval: 600000
      }
    );

  return (
    <TouchpointsCountContext.Provider
      value={{
        assignedTouchpointsCount
      }}
    >
      {children}
    </TouchpointsCountContext.Provider>
  );
}

export default function useTouchpointsCount() {
  const context = useContext(TouchpointsCountContext);
  if (!context) {
    throw new Error(
      `useTouchpointsCount must be used within a TouchpointsCountProvider`
    );
  }
  const { assignedTouchpointsCount } = context;
  return {
    assignedTouchpointsCount
  };
}
